<template>
  <div class="review-card">
    <!-- Header: Status + Submitter + Datum -->
    <div class="rc-header">
      <span class="status-pill" :class="`status-${statusMeta.tone}`">
        <v-icon size="11">{{ statusMeta.icon }}</v-icon>
        {{ statusMeta.label }}
      </span>
      <div class="rc-meta">
        <template v-if="review.submittedByEmployee">
          <v-avatar size="20" class="submitter-avatar">
            <v-img v-if="review.submittedByEmployee.avatar" :src="review.submittedByEmployee.avatar">
              <template #error><span class="ava-initials">{{ initials(review.submittedByEmployee) }}</span></template>
            </v-img>
            <span v-else class="ava-initials">{{ initials(review.submittedByEmployee) }}</span>
          </v-avatar>
          <span class="submitter-name">{{ review.submittedByEmployee.firstname }} {{ review.submittedByEmployee.lastname }}</span>
        </template>
        <span class="submitter-date">{{ formatDate(review.createdAt) }}</span>
      </div>
    </div>

    <!-- Stepper -->
    <div class="stepper">
      <div class="step">
        <div class="step-dot" :class="stageClass('technical')">
          <v-icon size="13">{{ stageIcon('technical') }}</v-icon>
        </div>
        <div class="step-body">
          <div class="step-title" :class="stageLabelClass('technical')">Technisch</div>
          <div v-if="review.technicalReviewerEmployee" class="step-sub">
            {{ review.technicalReviewerEmployee.firstname }} {{ review.technicalReviewerEmployee.lastname }}
          </div>
        </div>
      </div>
      <div class="step-connector" :class="{ 'step-connector-done': stageDone('technical') }"></div>
      <div class="step">
        <div class="step-dot" :class="stageClass('visual')">
          <v-icon size="13">{{ stageIcon('visual') }}</v-icon>
        </div>
        <div class="step-body">
          <div class="step-title" :class="stageLabelClass('visual')">Optisch</div>
          <div v-if="review.visualReviewerEmployee" class="step-sub">
            {{ review.visualReviewerEmployee.firstname }} {{ review.visualReviewerEmployee.lastname }}
          </div>
        </div>
      </div>
    </div>

    <!-- Felder -->
    <div class="fields">
      <div class="field-row">
        <v-icon size="13" color="#999">mdi-pound</v-icon>
        <span class="field-strong">{{ review.testNumber }}</span>
      </div>
      <div class="field-row">
        <v-icon size="13" color="#999">mdi-file-outline</v-icon>
        <span>{{ review.testPage }}</span>
      </div>
      <div class="field-row">
        <v-icon size="13" color="#999">{{ deviceIcon(review.device) }}</v-icon>
        <span>{{ review.device }}</span>
      </div>
      <div class="field-row">
        <v-icon size="13" :color="review.ablyftChecked === 'yes' ? '#2E7D32' : '#E65100'">
          {{ review.ablyftChecked === 'yes' ? 'mdi-check-circle-outline' : 'mdi-alert-outline' }}
        </v-icon>
        <span>ABLyft: {{ review.ablyftChecked === 'yes' ? 'Angelegt & geprüft' : 'Noch nicht angelegt' }}</span>
      </div>
      <div v-if="review.deadline" class="field-row">
        <v-icon size="13" color="#999">mdi-calendar-outline</v-icon>
        <span>Deadline: {{ review.deadline }}</span>
      </div>
      <div v-if="review.ablyftExperimentName" class="field-row">
        <v-icon size="13" color="#5B5BD6">mdi-flask-outline</v-icon>
        <a v-if="ablyftUrl" :href="ablyftUrl" target="_blank" class="field-link">
          {{ review.ablyftExperimentName }}
          <v-icon size="10">mdi-open-in-new</v-icon>
        </a>
        <span v-else>{{ review.ablyftExperimentName }}</span>
      </div>
    </div>

    <!-- Weitere Infos -->
    <div v-if="review.additionalInfo" class="extra-info">
      <div class="extra-label">Weitere Informationen</div>
      <div class="extra-text">{{ review.additionalInfo }}</div>
    </div>

    <!-- Screenshots -->
    <div v-if="screenshotCount > 0" class="screenshots-block">
      <div class="extra-label">Screenshots</div>
      <div class="screenshots-grid">
        <div
          v-for="i in screenshotCount"
          :key="i - 1"
          class="screenshot-thumb"
          @click="$emit('open-screenshot', screenshotUrl(i - 1))"
        >
          <v-img :src="screenshotUrl(i - 1)" aspect-ratio="1.4" cover />
        </div>
      </div>
    </div>

    <!-- Technische Ablehnung -->
    <div v-if="hasTechnicalRejection" class="rejection-box">
      <div class="rejection-head">
        <v-icon size="12" color="#D32F2F">mdi-code-tags</v-icon>
        <span class="rejection-title">Technisch abgelehnt</span>
        <span v-if="review.technicalReviewerEmployee" class="rejection-by">
          · {{ review.technicalReviewerEmployee.firstname }} {{ review.technicalReviewerEmployee.lastname }}
        </span>
      </div>
      <div class="rejection-text">{{ review.technicalRejectionReason }}</div>
      <div
        v-if="review.hasTechnicalRejectionScreenshot"
        class="rejection-img"
        @click="$emit('open-screenshot', rejectionScreenshotUrl('technical'))"
      >
        <v-img :src="rejectionScreenshotUrl('technical')" max-height="120" cover />
      </div>
    </div>

    <!-- Optische Ablehnung -->
    <div v-if="hasVisualRejection" class="rejection-box">
      <div class="rejection-head">
        <v-icon size="12" color="#D32F2F">mdi-eye-outline</v-icon>
        <span class="rejection-title">Optisch abgelehnt</span>
        <span v-if="review.visualReviewerEmployee" class="rejection-by">
          · {{ review.visualReviewerEmployee.firstname }} {{ review.visualReviewerEmployee.lastname }}
        </span>
      </div>
      <div class="rejection-text">{{ review.visualRejectionReason }}</div>
      <div
        v-if="review.hasVisualRejectionScreenshot"
        class="rejection-img"
        @click="$emit('open-screenshot', rejectionScreenshotUrl('visual'))"
      >
        <v-img :src="rejectionScreenshotUrl('visual')" max-height="120" cover />
      </div>
    </div>

    <!-- Reviewer-Actions -->
    <div v-if="showReviewerActions" class="actions">
      <template v-if="canActAsReviewer">
        <button class="btn-accept" :disabled="acceptingId === review.id" @click="$emit('accept', review)">
          <v-progress-circular v-if="acceptingId === review.id" indeterminate size="14" width="2" color="white" />
          <v-icon v-else size="15">mdi-check</v-icon>
          <span>Akzeptieren</span>
        </button>
        <button class="btn-reject" @click="$emit('reject', review)">
          <v-icon size="15">mdi-close</v-icon>
          <span>Ablehnen</span>
        </button>
      </template>
      <div v-else-if="isSelfReview" class="info-note">
        <v-icon size="14" color="#5B5BD6">mdi-information-outline</v-icon>
        Eigene Reviews können nicht selbst bestätigt werden
      </div>
    </div>

    <!-- Submitter-Actions -->
    <div v-if="showSubmitterActions && (canResubmit || canArchive || canUnarchive)" class="actions">
      <button v-if="canResubmit" class="btn-secondary btn-resubmit" :disabled="resubmittingId === review.id" @click="$emit('resubmit', review)">
        <v-progress-circular v-if="resubmittingId === review.id" indeterminate size="14" width="2" color="#5B5BD6" />
        <v-icon v-else size="15">mdi-wrench-outline</v-icon>
        <span>Als gefixt einreichen</span>
      </button>
      <button v-if="canArchive" class="btn-secondary" :disabled="archivingId === review.id" @click="$emit('archive', review)">
        <v-progress-circular v-if="archivingId === review.id" indeterminate size="14" width="2" color="#666" />
        <v-icon v-else size="15">mdi-archive-outline</v-icon>
        <span>Archivieren</span>
      </button>
      <button v-if="canUnarchive" class="btn-secondary" :disabled="archivingId === review.id" @click="$emit('unarchive', review)">
        <v-progress-circular v-if="archivingId === review.id" indeterminate size="14" width="2" color="#666" />
        <v-icon v-else size="15">mdi-archive-arrow-up-outline</v-icon>
        <span>Aus Archiv holen</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReviewCard',
  props: {
    review: { type: Object, required: true },
    currentUser: { type: Object, default: () => ({}) },
    context: { type: String, default: 'mine' },
    acceptingId: { type: [Number, String, null], default: null },
    resubmittingId: { type: [Number, String, null], default: null },
    archivingId: { type: [Number, String, null], default: null },
  },
  emits: ['accept', 'reject', 'resubmit', 'archive', 'unarchive', 'open-screenshot'],

  computed: {
    screenshotCount() { return Number(this.review.screenshotCount) || 0; },
    cacheKey() { return this.review.updatedAt ? new Date(this.review.updatedAt).getTime() : 0; },
    activeStage() {
      const s = this.review.status;
      if (['pending_technical', 'fix_pending_technical', 'rejected_technical'].includes(s)) return 'technical';
      if (['pending_visual', 'fix_pending_visual', 'rejected_visual'].includes(s)) return 'visual';
      if (s === 'approved') return 'done';
      return null;
    },
    isSelfReview() {
      return this.currentUser && this.review.submittedBy &&
        String(this.currentUser.id) === String(this.review.submittedBy);
    },
    isPendingForStage() {
      const s = this.review.status;
      if (this.context === 'technical') return ['pending_technical', 'fix_pending_technical'].includes(s);
      if (this.context === 'visual') return ['pending_visual', 'fix_pending_visual'].includes(s);
      return false;
    },
    showReviewerActions() {
      return (this.context === 'technical' || this.context === 'visual') && this.isPendingForStage;
    },
    canActAsReviewer() {
      if (this.isSelfReview) return false;
      if (this.context === 'technical') return !!this.currentUser?.isTechnicalReviewer;
      if (this.context === 'visual') return !!this.currentUser?.isVisualReviewer;
      return false;
    },
    showSubmitterActions() { return this.context === 'mine'; },
    canResubmit() {
      return this.context === 'mine' &&
        (this.review.status === 'rejected_technical' || this.review.status === 'rejected_visual');
    },
    isTerminal() {
      return ['approved', 'rejected_technical', 'rejected_visual'].includes(this.review.status);
    },
    canArchive() { return this.context === 'mine' && this.isTerminal && !this.review.archivedBySubmitter; },
    canUnarchive() { return this.context === 'mine' && !!this.review.archivedBySubmitter; },
    hasTechnicalRejection() {
      return !!this.review.technicalRejectionReason &&
        ['rejected_technical', 'fix_pending_technical', 'pending_visual', 'rejected_visual', 'fix_pending_visual', 'approved'].includes(this.review.status);
    },
    hasVisualRejection() {
      return !!this.review.visualRejectionReason &&
        ['rejected_visual', 'fix_pending_visual', 'approved'].includes(this.review.status);
    },
    statusMeta() { return this.statusMetadata(this.review.status); },
    ablyftUrl() {
      if (!this.review.ablyftProjectId || !this.review.ablyftExperimentId) return null;
      return `https://app.ablyft.com/team/1094/projects/${this.review.ablyftProjectId}/experiments/${this.review.ablyftExperimentId}`;
    },
  },

  methods: {
    screenshotUrl(index) { return `/api/reviews/${this.review.id}/screenshot/${index}?v=${this.cacheKey}`; },
    rejectionScreenshotUrl(stage) { return `/api/reviews/${this.review.id}/rejection-screenshot/${stage}?v=${this.cacheKey}`; },
    statusMetadata(status) {
      const map = {
        pending_technical:     { label: 'Technisch in Prüfung', tone: 'warning', icon: 'mdi-clock-outline' },
        fix_pending_technical: { label: 'Fix – Technisch',      tone: 'info',    icon: 'mdi-wrench-outline' },
        rejected_technical:    { label: 'Technisch abgelehnt',  tone: 'danger',  icon: 'mdi-close-circle-outline' },
        pending_visual:        { label: 'Optisch in Prüfung',   tone: 'warning', icon: 'mdi-clock-outline' },
        fix_pending_visual:    { label: 'Fix – Optisch',        tone: 'info',    icon: 'mdi-wrench-outline' },
        rejected_visual:       { label: 'Optisch abgelehnt',    tone: 'danger',  icon: 'mdi-close-circle-outline' },
        approved:              { label: 'Approved',             tone: 'success', icon: 'mdi-check-all' },
      };
      return map[status] || { label: status, tone: 'neutral', icon: 'mdi-help-circle-outline' };
    },
    stageDone(stage) {
      const s = this.review.status;
      if (stage === 'technical') return ['pending_visual', 'fix_pending_visual', 'rejected_visual', 'approved'].includes(s);
      if (stage === 'visual') return s === 'approved';
      return false;
    },
    stageActive(stage) {
      const s = this.review.status;
      if (stage === 'technical') return ['pending_technical', 'fix_pending_technical'].includes(s);
      if (stage === 'visual') return ['pending_visual', 'fix_pending_visual'].includes(s);
      return false;
    },
    stageRejected(stage) {
      const s = this.review.status;
      if (stage === 'technical') return s === 'rejected_technical';
      if (stage === 'visual') return s === 'rejected_visual';
      return false;
    },
    stageClass(stage) {
      if (this.stageDone(stage)) return 'step-dot-done';
      if (this.stageActive(stage)) return 'step-dot-active';
      if (this.stageRejected(stage)) return 'step-dot-rejected';
      return 'step-dot-upcoming';
    },
    stageLabelClass(stage) {
      if (this.stageDone(stage)) return 'lbl-done';
      if (this.stageActive(stage)) return 'lbl-active';
      if (this.stageRejected(stage)) return 'lbl-rejected';
      return 'lbl-upcoming';
    },
    stageIcon(stage) {
      if (this.stageDone(stage)) return 'mdi-check';
      if (this.stageRejected(stage)) return 'mdi-close';
      if (stage === 'technical') return 'mdi-code-tags';
      return 'mdi-eye-outline';
    },
    initials(emp) {
      if (!emp) return '?';
      return (emp.firstname?.charAt(0) || '') + (emp.lastname?.charAt(0) || '');
    },
    deviceIcon(device) {
      if (device === 'Mobile') return 'mdi-cellphone';
      if (device === 'Desktop') return 'mdi-monitor';
      return 'mdi-cellphone-link';
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.review-card {
  background: white;
  border: 1px solid #eceef2;
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: border-color 0.15s;
}
.review-card:hover { border-color: #c5cbd3; }

.rc-header { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.rc-meta { display: flex; align-items: center; gap: 8px; min-width: 0; }
.submitter-avatar { background: #e8eaed; }
.ava-initials { font-size: 10px; font-weight: 700; color: #0a1628; }
.submitter-name { font-size: 12px; color: #555; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.submitter-date { font-size: 11px; color: #999; white-space: nowrap; }

/* Status Pill */
.status-pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 20px;
  font-size: 11px; font-weight: 700;
}
.status-warning { background: rgba(255,152,0,0.1); color: #E65100; }
.status-info    { background: rgba(91,91,214,0.1); color: #5B5BD6; }
.status-danger  { background: rgba(211,47,47,0.08); color: #D32F2F; }
.status-success { background: rgba(46,125,50,0.1); color: #2E7D32; }
.status-neutral { background: #f0f2f5; color: #666; }

/* Stepper */
.stepper {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fafbfc;
  border: 1px solid #eceef2;
  border-radius: 10px;
}
.step { display: flex; align-items: center; gap: 10px; min-width: 0; }
.step-dot {
  width: 28px; height: 28px; min-width: 28px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid transparent;
  transition: all 0.15s;
}
.step-dot-done     { background: rgba(46,125,50,0.12);  border-color: rgba(46,125,50,0.4);  color: #2E7D32; }
.step-dot-active   { background: rgba(255,152,0,0.12);  border-color: rgba(255,152,0,0.4);  color: #E65100; }
.step-dot-rejected { background: rgba(211,47,47,0.1);   border-color: rgba(211,47,47,0.4);  color: #D32F2F; }
.step-dot-upcoming { background: #f0f2f5;               border-color: #e0e2e6;              color: #bbb; }
.step-body { min-width: 0; }
.step-title { font-size: 12px; font-weight: 700; }
.step-sub { font-size: 11px; color: #888; margin-top: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lbl-done     { color: #2E7D32; }
.lbl-active   { color: #0a1628; }
.lbl-rejected { color: #D32F2F; }
.lbl-upcoming { color: #aaa; }
.step-connector { height: 2px; background: #e0e2e6; border-radius: 2px; min-width: 20px; }
.step-connector-done { background: rgba(46,125,50,0.4); }

/* Fields */
.fields { display: flex; flex-direction: column; gap: 6px; }
.field-row { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #555; }
.field-strong { color: #0a1628; font-weight: 700; }
.field-link { color: #5B5BD6; text-decoration: none; display: inline-flex; align-items: center; gap: 4px; }
.field-link:hover { text-decoration: underline; }

/* Extra info */
.extra-info { display: flex; flex-direction: column; gap: 4px; }
.extra-label { font-size: 11px; font-weight: 600; color: #999; text-transform: uppercase; letter-spacing: 0.3px; }
.extra-text { font-size: 13px; color: #555; white-space: pre-wrap; line-height: 1.5; }

.screenshots-block { display: flex; flex-direction: column; gap: 6px; }
.screenshots-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.screenshot-thumb {
  border: 1px solid #eceef2; border-radius: 8px; overflow: hidden;
  cursor: zoom-in; transition: opacity 0.15s;
}
.screenshot-thumb:hover { opacity: 0.85; }

/* Rejection */
.rejection-box {
  background: rgba(211,47,47,0.05);
  border: 1px solid rgba(211,47,47,0.15);
  border-radius: 10px; padding: 12px;
}
.rejection-head { display: flex; align-items: center; gap: 4px; margin-bottom: 6px; }
.rejection-title { font-size: 11px; font-weight: 700; color: #D32F2F; }
.rejection-by { font-size: 11px; color: #999; }
.rejection-text { font-size: 13px; color: #555; white-space: pre-wrap; line-height: 1.5; }
.rejection-img { margin-top: 8px; border-radius: 8px; overflow: hidden; cursor: zoom-in; }

/* Actions */
.actions { display: flex; gap: 8px; flex-wrap: wrap; }

.btn-accept, .btn-reject, .btn-secondary {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  flex: 1; min-height: 36px; padding: 0 14px;
  border-radius: 9px; font-size: 12px; font-weight: 700;
  cursor: pointer; transition: all 0.15s; border: 1px solid transparent;
  font-family: inherit;
  white-space: nowrap;
}
.btn-accept { background: #2E7D32; color: white; }
.btn-accept:hover:not(:disabled) { background: #1B5E20; }
.btn-accept:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-reject { background: white; color: #D32F2F; border-color: rgba(211,47,47,0.25); }
.btn-reject:hover { background: rgba(211,47,47,0.06); border-color: #D32F2F; }

.btn-secondary { background: white; color: #555; border-color: #eceef2; }
.btn-secondary:hover:not(:disabled) { border-color: #0a1628; color: #0a1628; }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-resubmit { color: #5B5BD6; border-color: rgba(91,91,214,0.25); }
.btn-resubmit:hover:not(:disabled) { background: rgba(91,91,214,0.06); border-color: #5B5BD6; color: #5B5BD6; }

.info-note {
  flex: 1; display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 12px; background: rgba(91,91,214,0.08); color: #3A3AC0;
  border-radius: 9px; font-size: 12px; font-weight: 500;
}
</style>
